import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FocusTrap } from 'focus-trap-react';
import useModalStore from '../../../zustand/store/useModalStore';
import useConfirm from '../../../hooks/useConfirm';
import { useModalOpen } from '../../../hooks/useToastModalOpen';
import { useDynamicStyle } from '../../../hooks/useDynamicStyle';
import { ROLE_API, handleRoleApiError, validateRoleForm } from './roleMngApi';

import '../../../css/modal.css';
import '../../../css/role-mng.css';

const formInit = {
    authjobs: '',
    roleno: '',
    rolename: '',
    useyn: 'Y',
    defaultyn: 'N',
    bigo: '',
};

/**
 * 롤 추가 모달 (롤권한관리 > 추가)
 * @param open 모달 표시 여부
 * @param setOpen 모달 표시 setter
 * @param authList 권한코드 목록 (EMIMBSCD EMIMUSER/AUTH)
 * @param onSaved 등록 성공 콜백 (등록된 roleno 전달)
 */
const RoleAdd_layer = ({ open, setOpen, authList, onSaved }) => {

    const [form, setForm] = useState(formInit);
    const [rolenoTouched, setRolenoTouched] = useState(false);
    const [saving, setSaving] = useState(false);

    const showNotiModal = useModalStore(state => state.showNotiModal);
    const confirm = useConfirm();
    const { toastModalOpen } = useModalOpen();
    const { primaryBackColor } = useDynamicStyle();

    const close = useCallback(() => {
        setForm(formInit);
        setRolenoTouched(false);
        setOpen(false);
    }, [setOpen]);

    // esc 키 누르면 모달 닫히는 기능 (확인/알림창이 떠 있을 때는 그 창만 닫히도록 무시)
    useEffect(() => {
        if (!open) return undefined;
        const handleEscape = (e) => {
            if (e.key !== 'Escape') return;
            const { confirmModal, notiModal, promptModal } = useModalStore.getState();
            if (confirmModal || notiModal || promptModal) return;
            close();
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [open, close]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (checked ? 'Y' : 'N') : value
        }));
        if (name === 'roleno') setRolenoTouched(true);
    };

    // 권한코드 선택 시 미사용 롤코드 자동 제안 (직접 입력한 값이 있으면 유지)
    const handleAuthChange = async (e) => {
        const authjobs = e.target.value;
        setForm(prev => ({ ...prev, authjobs }));
        if (!authjobs || rolenoTouched) return;
        try {
            const response = await axios.get(ROLE_API.nextRoleNo, { params: { authjobs } });
            setForm(prev => ({ ...prev, roleno: response.data.roleno ?? '' }));
        } catch (error) {
            handleRoleApiError(error, showNotiModal, '롤코드 제안 조회 중 오류가 발생했습니다.');
        }
    };

    const save = async () => {
        const error = validateRoleForm(form);
        if (error) {
            showNotiModal(error);
            return;
        }
        const ok = await confirm(`롤 [${form.rolename.trim()}] (롤코드 ${form.roleno})을(를) 등록하시겠습니까?`);
        if (!ok) return;

        try {
            setSaving(true);
            const response = await axios.post(ROLE_API.insert, {
                authjobs: form.authjobs,
                roleno: Number(form.roleno),
                rolename: form.rolename.trim(),
                useyn: form.useyn,
                defaultyn: form.defaultyn,
                bigo: form.bigo.trim(),
            });
            const { result, resultMessage } = response.data.resultMap;
            if (result) {
                toastModalOpen(resultMessage);
                onSaved(Number(form.roleno));
                close();
            } else {
                showNotiModal(resultMessage);
            }
        } catch (error) {
            handleRoleApiError(error, showNotiModal, '롤 등록 중 오류가 발생했습니다.');
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    return (
        <FocusTrap focusTrapOptions={{ fallbackFocus: '#role-add-title' }}>
            <div className='layer_container role_layer'>
                <div className='layer_wrapper' role='dialog' aria-modal='true' aria-labelledby='role-add-title'>
                    <div className='modal_title_box mb-2'>
                        <h4 id='role-add-title' tabIndex={-1}>롤 추가</h4>
                    </div>
                    <div className='com-search role_form'>
                        <table>
                            <colgroup>
                                <col width='22%' />
                                <col width='78%' />
                            </colgroup>
                            <tbody>
                                <tr>
                                    <th><label htmlFor='role-add-authjobs'>권한코드 <span className='co_red'>*</span></label></th>
                                    <td>
                                        <select id='role-add-authjobs' name='authjobs' className='w-full' value={form.authjobs} onChange={handleAuthChange} required>
                                            <option value=''>==권한코드 선택==</option>
                                            {authList?.map((item) => (
                                                <option key={item.bscdminr} value={item.bscdminr}>{item.bscdminr} - {item.bscddesc}</option>
                                            ))}
                                        </select>
                                    </td>
                                </tr>
                                <tr>
                                    <th><label htmlFor='role-add-roleno'>롤코드 <span className='co_red'>*</span></label></th>
                                    <td>
                                        <div id='role-add-roleno-hint' className='role_form_note mb-1'>※ 권한코드 선택 시 미사용 롤코드가 자동 제안되며 직접 수정할 수 있습니다.</div>
                                        <input id='role-add-roleno' type='number' name='roleno' min='1' max='9999999999' step='1'
                                            value={form.roleno} onChange={handleInputChange} aria-describedby='role-add-roleno-hint' required />
                                    </td>
                                </tr>
                                <tr>
                                    <th><label htmlFor='role-add-rolename'>롤명 <span className='co_red'>*</span></label></th>
                                    <td>
                                        <input id='role-add-rolename' type='text' name='rolename' maxLength={50} value={form.rolename} onChange={handleInputChange} required />
                                    </td>
                                </tr>
                                <tr>
                                    <th><label htmlFor='role-add-useyn'>사용여부</label></th>
                                    <td>
                                        <div className='flex gap-4 items-center'>
                                            <select id='role-add-useyn' name='useyn' value={form.useyn} onChange={handleInputChange}>
                                                <option value='Y'>사용</option>
                                                <option value='N'>미사용</option>
                                            </select>
                                            <label htmlFor='role-add-defaultyn'>
                                                <input type='checkbox' id='role-add-defaultyn' name='defaultyn' checked={form.defaultyn === 'Y'} onChange={handleInputChange} />
                                                기본롤 (권한코드당 1개)
                                            </label>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <th><label htmlFor='role-add-bigo'>비고</label></th>
                                    <td>
                                        <input id='role-add-bigo' type='text' name='bigo' maxLength={2000} value={form.bigo} onChange={handleInputChange} />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className='modal_btn_box mt-2'>
                        <button className={`modal_btn ${primaryBackColor}`} onClick={save} disabled={saving}>등록</button>
                        <button className='modal_btn bg_co_black hover_co_dark_bg' onClick={close}>닫기</button>
                    </div>
                </div>
            </div>
        </FocusTrap>
    );
};

export default RoleAdd_layer;
